import Brand from '../components/Brand'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'

export default function LoginPage() {
  const { signIn, signUp } = useAuth()
  const navigate = useNavigate()
  const [mode, setMode] = useState('login')       // 'login' | 'signup'
  const [role, setRole] = useState('client')       // 'cpa' | 'client'
  const [form, setForm] = useState({ email: '', password: '', fullName: '', practiceName: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }))

  async function handleSubmit(e) {
    e.preventDefault()
    setError(''); setSuccess(''); setLoading(true)

    if (mode === 'login') {
      const result = await signIn(form.email, form.password)
      const { error } = result
      if (error) { setError(error.message); setLoading(false); return }
      if (result.mustResetPassword) {
        navigate('/reset-password?required=true')
        return
      }
      navigate('/dashboard')
    } else {
      if (!form.fullName.trim()) { setError('Please enter your full name'); setLoading(false); return }
      const { error } = await signUp(form.email, form.password, form.fullName, role)
      if (error) { setError(error.message); setLoading(false); return }
      setSuccess('Account created! Check your email to confirm, then sign in.')
      setMode('login')
    }
    setLoading(false)
  }

  return (
    <div className="login-page-root" style={{
      minHeight: '100vh', display: 'flex',
      background: '#202326',
    }}>
      <style>{`
        @media (max-width: 768px) {
          .login-page-root {
            flex-direction: column;
          }
          .login-card {
            padding: 26px 20px !important;
            border-radius: 12px !important;
          }
          .hide-mobile {
            display: none !important;
          }
        }
      `}</style>
      <section className="left-panel cf-login-intro" style={{ flexDirection: 'column', justifyContent: 'center' }}>
        <a href="/" aria-label="ClearFile home"><Brand light/></a>
        <h2>Clear work.<br/><span>Confident next steps.</span></h2>
        <p>Your documents, conversations and payments, together in one organized workspace.</p>
        <a className="cf-login-home" href="/">← Back to ClearFile</a>
      </section>

      {/* Center card */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '24px'
      }}>
        <div className="animate-fadeUp login-card" style={{
          background: '#fff', borderRadius: 16, padding: '44px 40px',
          width: '100%', maxWidth: 440,
          boxShadow: '0 24px 80px rgba(0,0,0,0.3)'
        }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{
              width: 52, height: 52, borderRadius: 14,
              background: '#CBB17B',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 24, fontWeight: 700, color: '#202326',
              fontFamily: "'DM Sans', sans-serif",
              boxShadow: '0 4px 16px rgba(201,168,76,0.35)',
              marginBottom: 12
            }}>C</div>
            <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 28, color: '#202326', marginBottom: 4 }}>
              ClearFile
            </h1>
            <p style={{ fontSize: 13, color: '#62615D' }}>
              {mode === 'login' ? 'Sign in to your account' : 'Create your account'}
            </p>
          </div>

          {/* Mode toggle */}
          <div style={{
            display: 'flex', background: '#F7F3EB', borderRadius: 10,
            padding: 4, marginBottom: 24, gap: 4
          }}>
            {[['login','Sign In'], ['signup','Create Account']].map(([m, label]) => (
              <button key={m} onClick={() => { setMode(m); setError(''); setSuccess('') }} style={{
                flex: 1, padding: '8px', borderRadius: 8, border: 'none',
                fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
                background: mode === m ? '#202326' : 'transparent',
                color: mode === m ? '#CBB17B' : '#62615D',
                cursor: 'pointer'
              }}>{label}</button>
            ))}
          </div>

          {/* Role selector (signup only) */}
          {mode === 'signup' && (
            <div style={{ marginBottom: 20 }}>
              <label className="label">I am a</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['client','Client / Business Owner'], ['cpa','Accountant / Tax Professional']].map(([r, label]) => (
                  <button key={r} onClick={() => setRole(r)} style={{
                    flex: 1, padding: '10px 12px', borderRadius: 8, border: '2px solid',
                    fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                    borderColor: role === r ? '#CBB17B' : '#DED6CA',
                    background: role === r ? '#F1E8D7' : '#fff',
                    color: role === r ? '#765B2C' : '#62615D',
                  }}>{label}</button>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {mode === 'signup' && (
              <div style={{ marginBottom: 16 }}>
                <label className="label" htmlFor="full-name">Full Name</label>
                <input id="full-name" autoComplete="name" className="input" placeholder="Jane Smith" value={form.fullName} onChange={set('fullName')} required />
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label className="label" htmlFor="email">Email Address</label>
              <input id="email" autoComplete="email" className="input" type="email" placeholder="you@example.com" value={form.email} onChange={set('email')} required />
            </div>

            <div style={{ marginBottom: mode === 'signup' ? 16 : 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label className="label" htmlFor="password" style={{ margin: 0 }}>Password</label>
                {mode === 'login' && (
                  <button type="button" onClick={async () => {
                    if (!form.email) { setError('Enter your email address first'); return }
                    setLoading(true)
                    const { error } = await supabase.auth.resetPasswordForEmail(form.email, {
                      redirectTo: 'https://clearfile.org/reset-password'
                    })
                    setLoading(false)
                    if (error) { setError(error.message); return }
                    setSuccess('Password reset email sent! Check your inbox.')
                  }} style={{ fontSize: 12, color: '#765B2C', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    Forgot password?
                  </button>
                )}
              </div>
              <input id="password" autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} className="input" type="password" placeholder={mode === 'signup' ? 'Min. 8 characters' : '••••••••'} value={form.password} onChange={set('password')} required minLength={8} />
            </div>

            {error && (
              <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
                {error}
              </div>
            )}
            {success && (
              <div style={{ background: '#D1FAE5', color: '#065F46', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
                {success}
              </div>
            )}

            <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', justifyContent: 'center', padding: '12px', fontSize: 14 }}>
              {loading ? (
                <span style={{ width: 16, height: 16, border: '2px solid rgba(201,168,76,0.3)', borderTopColor: '#CBB17B', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
              ) : mode === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          <p style={{ textAlign: 'center', fontSize: 12, color: '#62615D', marginTop: 24 }}>
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(''); setSuccess('') }}
              style={{ color: '#765B2C', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>
              {mode === 'login' ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>

    </div>
  )
}
